// src/views/menage/PanneauMenage.jsx — LE PANNEAU DU MÉNAGE (maquette validée
// Dimitri 2026-08-19). Il REMPLACE le contenu du détail de la frise
// (ex-DetailBloc) ; le mécanisme d'ouverture — menu → « Détails du ménage » —
// ne change pas.
//
// CINQ BLOCS, dans l'ordre : bandeau navy · qui fait le ménage · qui contrôle ·
// le travail de l'agent · le logement · les incidents. RÈGLE : les blocs ne
// disparaissent jamais, ils EXPLIQUENT (« Un ménage se contrôle une fois
// terminé. ») — SEULE exception : le bloc incidents disparaît s'il est vide.
//
// QUI VOIT QUOI (recadrage Dimitri 2026-08-19) : « contrôlé par [prénom] »
// s'affiche pour l'ADMIN ET L'ORCHESTRATEUR — le SEUL profil qui ne doit
// jamais savoir qui a contrôlé est l'AGENT CONTRÔLÉ, et il ne passe pas par ce
// panneau (vue Pilotage, rôles pilotes seulement).
//
// TOUT SE CALCULE AU RENDU depuis le ctx (règle guichet CLAUDE.md) : le
// panneau ne fait AUCUNE requête — les batches vivent au niveau vue
// (MenageSuiviView) et arrivent par le ctx. Optimisme/rollback des écritures :
// au niveau vue aussi (onReassign / onRetirerAssignation /
// onDesignerControleur), le panneau ne fait que déclencher.

import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import FeuilleConfirmation from '../../components/FeuilleConfirmation'
import PortailFeuille from '../../components/PortailFeuille'
import useVoileEcran from '../../lib/useVoileEcran'
import useEtroit from '../../lib/useEtroit'
import { DS } from '../../lib/ui'
import useEchapPourFermer from '../../lib/useEchapPourFermer'
import { firstNameFromEmail, libelleDepuisEmail, initialsFromEmail } from './RunCard'
import { formaterDuree, formaterHeureCourte } from '../../lib/menage-frise'
import { libelleException, actionRetourALaRegle } from '../../lib/menage-exception'
// Imports LECTURE SEULE depuis l'écran Contrôle — le vocabulaire du chantier
// vit là-bas, on ne le réécrit pas ici.
import { depuisTexte, lignesChecklist } from './ControleView'
import { normalizeStatus, STATUS as STATUS_INCIDENTS } from '../../lib/incidents-api'
import { libelleHeures } from '../../lib/menage-frise'
import {
  AlertTriangle, CircleCheck, ChevronRight, ChevronDown, ClipboardList,
  Navigation, Eye, EyeOff, Timer, X, Info, User, Home, Key, Hourglass,
  CalendarDays, Check, LogOut, LogIn,
} from 'lucide-react'

// ── PALETTE DU PANNEAU — chaque valeur est actée par la maquette. ────────────
const P = {
  navyFonce: '#233240', navy: '#3d5166', ciel: '#5c8ab8',
  vert: '#7fc4a3', vertSombre: '#1a4f3a', vertBord: '#c9e0d4', vertFond: '#f0f7f3',
  terra: '#b8412b', terraTexte: '#c1512d', terraSombre: '#8f2f1d',
  peau: '#f0c4b6',
  cielFond: '#e8f2fa', cielBord: '#c3d5e4',
  controleFond: '#fbfdfe', controlePasseFond: '#fdf9f7',
  incidentFond: '#fdf4f0', incidentBord: '#e0b5a5', incidentSigne: '#a8664f',
  regleBord: '#e3e8ee',
  expliqueFond: '#f6f8fa',
  bord: '#d5e3ee', gris: '#5c6773', muet: '#98a2ad', nom: '#233240',
}

/** « lundi 17 août » — la date du ménage, en français, minuscule (maquette). */
export function dateBandeau(iso) {
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso || ''
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
}

/** « il y a 2 h » — l'âge d'un signalement (miroir de depuisTexte, autre préposition). */
export function ilYATexte(iso) {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const min = Math.floor(ms / 60000)
  if (min < 60) return `il y a ${Math.max(min, 1)} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  return `il y a ${Math.floor(h / 24)} j`
}

// ── LA CARTE D'ÉTAT D'UN CONTRÔLE — vocabulaire EXACT du chantier (miroir de
// SectionMesControles, ControleView). `a_reprendre` legacy = reprise en cours.
export function etatControleTexte(controle, prenomAgent) {
  const n = Array.isArray(controle?.points) ? controle.points.length : 0
  const pts = `${n} point${n > 1 ? 's' : ''}`
  const depuis = depuisTexte(controle?.cree_le)
  if (controle?.statut === 'bon') return { attend: false, texte: 'rien à signaler' }
  if (controle?.statut === 'regle') return { attend: false, texte: `${pts} · repris` }
  if (controle?.reprise_par === 'controleur') {
    return { attend: true, texte: `${pts} · je reprends${depuis ? ` · ${depuis}` : ''}` }
  }
  const qui = prenomAgent || "l'agent"
  return { attend: true, texte: `${pts} · ${qui} reprend${depuis ? ` · ${depuis}` : ''}` }
}

/** Un contrôle attend-il une reprise ? (ton « attente » de la bande) */
export function controleAttend(controle) {
  return !!controle && (controle.statut === 'a_reprendre' || controle.statut === 'reprise_en_cours')
}

// ── LES CODES D'ACCÈS — l'ordre est acté : boîte à clés EN TÊTE. ────────────
const ORDRE_CODES = [
  ['boite_cles', 'BOÎTE À CLÉS'],
  ['porte_entree', "PORTE D'ENTRÉE"],
  ['interphone', 'INTERPHONE'],
  ['coffre', 'COFFRE'],
  ['garage', 'GARAGE'],
]

/** codes_acces → [{ cle, label, code }] (renseignés seulement, dans l'ordre). */
export function lignesCodes(codesAcces) {
  const codes = codesAcces && typeof codesAcces === 'object' ? codesAcces : {}
  const out = []
  for (const [cle, label] of ORDRE_CODES) {
    const brut = codes[cle]
    const code = brut == null ? '' : String(brut).trim()
    if (code) out.push({ cle, label, code })
  }
  return out
}

// ── Titre de bloc : 9.5 px majuscules, pastille RONDE 5 px devant. ──────────
// Recalage relief (maquette 2026-08-20) : un CARRÉ TEINTÉ 24 px avec la vraie
// icône du bloc — la pastille ronde de 5 px ne portait rien. Le titre prend la
// couleur de son bloc.
// v3 2026-08-19 : carrés PLEINS 26 px, icône blanche 15 px — plus de carrés
// pâles, chaque bloc s'annonce d'un coup d'œil.
function TitreBloc({ icone: Icone, fond, couleurIcone = '#fff', couleur, children, testId }) {
  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 8,
      fontSize: 9.5, fontWeight: 700, color: couleur || P.gris, textTransform: 'uppercase',
      letterSpacing: '.09em', fontFamily: DS.fontBody }}>
      <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 8,
        background: fond, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0 }}>
        {Icone && <Icone size={15} strokeWidth={2.1} color={couleurIcone} />}
      </span>
      {children}
    </div>
  )
}

// ── Un bloc qui EXPLIQUE au lieu de disparaître. ────────────────────────────
function BlocExplique({ texte, testId, pointille = false }) {
  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 8,
      background: pointille ? '#f8fafc' : P.expliqueFond,
      border: pointille ? '1.5px dashed #c3d5e4' : 'none',
      borderRadius: 8, padding: '9px 11px',
      fontSize: 11.5, color: P.gris, fontFamily: DS.fontBody }}>
      <Info size={13} strokeWidth={2} color={P.muet} style={{ flexShrink: 0 }} />
      {texte}
    </div>
  )
}

// ── Pastille d'agent 26 px (initiales). ─────────────────────────────────────
function PastilleInitiales({ email, fond, couleur }) {
  return (
    <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%',
      background: fond, color: couleur, display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 10, fontWeight: 700,
      fontFamily: DS.fontBody, flexShrink: 0 }}>
      {initialsFromEmail(email)}
    </span>
  )
}

// ── Une rangée d'agents en boutons 44 px — deux teintes (ménage / contrôle).─
function RangeeAgents({ options, actifEmail, teinte, onChoisir, testPrefix, sousLibelle }) {
  const t = teinte === 'vert'
    ? { bordActif: P.vertSombre, fondActif: P.vertFond, pastilleActif: P.vert,
        bord: P.vertBord }
    : { bordActif: P.navy, fondActif: P.cielFond, pastilleActif: P.ciel,
        bord: P.bord }
  return options.map((a) => {
    const actif = a.value === actifEmail
    return (
      <button key={a.value} type="button" data-testid={`${testPrefix}-${a.value}`}
        aria-pressed={actif}
        onClick={() => onChoisir(a.value)}
        className="fbz-press"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 44,
          padding: '0 12px 0 8px',
          border: actif ? `2px solid ${t.bordActif}` : `1px solid ${t.bord}`,
          background: actif ? t.fondActif : '#fff',
          borderRadius: 10, cursor: 'pointer', fontFamily: DS.fontBody }}>
        <PastilleInitiales email={a.value}
          fond={actif ? t.pastilleActif : P.cielFond}
          couleur={actif ? '#fff' : P.navy} />
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: P.nom }}>
            {a.label || libelleDepuisEmail(a.value)}
          </span>
          {actif && sousLibelle && (
            <span data-testid={`${testPrefix}-par-defaut`}
              style={{ fontSize: 9.5, color: P.gris }}>{sousLibelle}</span>
          )}
        </span>
      </button>
    )
  })
}

// ═══ LE PANNEAU ═════════════════════════════════════════════════════════════

export default function PanneauMenage({ bloc, ctx, onFermer = null }) {
  useVoileEcran()
  // TÉLÉPHONE (cadrage 2026-08-22) : le panneau MONTE DU BAS.
  const etroit = useEtroit()
  // FERMETURES STANDARD (tri Dimitri 2026-08-17) : Échap (hook pile) + clic à
  // l'extérieur — sans elles le panneau bloquait tout dessous (attrapé par la
  // pose du 2026-08-19).
  useEchapPourFermer(onFermer || (() => {}), !!onFermer)
  const racineRef = useRef(null)
  useEffect(() => {
    if (!onFermer) return undefined
    const surPointerDown = (e) => {
      if (racineRef.current && !racineRef.current.contains(e.target)) onFermer()
    }
    const timer = setTimeout(() => document.addEventListener('pointerdown', surPointerDown, true), 0)
    return () => { clearTimeout(timer); document.removeEventListener('pointerdown', surPointerDown, true) }
  }, [onFermer])

  const run = bloc.run || null
  const controle = run ? (ctx.controleParRun?.[run.id] || null) : null
  // ③ Exception à la règle d'assignation (réintégré 2026-08-19).
  const regleAssignation = ctx.regles?.[bloc.logementId] || null
  const exception = run ? libelleException(run, regleAssignation, ctx.nomAgent) : null
  const retourRegle = run ? actionRetourALaRegle(run, regleAssignation, ctx.assignOptions || []) : null
  const fini = run?.status === 'completed'
  const info = run ? ctx.infoFor?.(bloc.logementId, run.scheduled_date) : null

  // « Copié » 2 s — un retour PAR CODE, jamais global (deux codes copiés à la
  // suite ne doivent pas se voler le message). Échec → message discret.
  const [copie, setCopie] = useState({}) // { [cle]: 'ok' | 'echec' }
  const timersCopie = useRef({})
  useEffect(() => () => {
    for (const t of Object.values(timersCopie.current)) clearTimeout(t)
  }, [])
  const copier = useCallback((cle, code) => {
    const poser = (etat) => {
      setCopie((c) => ({ ...c, [cle]: etat }))
      clearTimeout(timersCopie.current[cle])
      timersCopie.current[cle] = setTimeout(() => {
        setCopie((c) => { const n = { ...c }; delete n[cle]; return n })
      }, 2000)
    }
    try {
      const p = navigator.clipboard?.writeText?.(code)
      if (p && typeof p.then === 'function') {
        p.then(() => poser('ok'), () => poser('echec'))
      } else if (p !== undefined) {
        poser('ok')
      } else {
        poser('echec')
      }
    } catch (e) {
      console.error('[panneau] copie code', e?.message || e)
      poser('echec')
    }
  }, [])

  // Incidents du panneau : logement du run + incident lié (run_id égal OU même
  // logement). ctx.incidentsPanneau null = batch en échec → bloc ABSENT
  // (best-effort, jamais un écran cassé).
  const incidents = useMemo(() => {
    const source = ctx.incidentsPanneau
    if (!Array.isArray(source) || !run) return null
    return source.filter((i) => i && (i.run_id === run.id || i.logement_id === bloc.logementId))
  }, [ctx.incidentsPanneau, run, bloc.logementId])
  const [reglesDeplies, setReglesDeplies] = useState(false)
  // ⚠️ NOTRE GRAMMAIRE, plus une boîte du navigateur (Dimitri 2026-08-22).
  const [confirmRetourRegle, setConfirmRetourRegle] = useState(false)

  // ── INTERVENTION : la maquette est celle du MÉNAGE — une intervention garde
  // un détail minimal (durée + suppression), rien d'autre à raconter.
  if (!run) {
    return (
      <PortailFeuille actif={etroit}>
      <div data-testid="panneau-voile"
        style={{ position: 'fixed', inset: 0, zIndex: 70,
          background: 'rgba(46,61,77,.5)', display: 'flex',
          alignItems: etroit ? 'flex-end' : 'center',
          justifyContent: 'center', padding: etroit ? 0 : 16 }}>
      <div ref={racineRef} data-testid={`panneau-intervention-${bloc.id}`}
        style={{ width: '100%', maxWidth: etroit ? 'none' : 430, maxHeight: '90vh', overflowY: 'auto',
          border: `1px solid ${P.bord}`, borderRadius: etroit ? '18px 18px 0 0' : 10,
          background: '#fff', fontFamily: DS.fontBody }}>
        <Bandeau nom={bloc.nom} sousTitre={libelleHeures(bloc.debut, bloc.fin)} pastille={null} />
        <div style={{ display: 'flex', gap: 8, padding: '11px 13px' }}>
          {ctx.onOuvrirDuree && (
            <button type="button" data-testid="panneau-intervention-duree"
              onClick={() => ctx.onOuvrirDuree(bloc)} className="fbz-press fbz-tap"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44,
                padding: '9px 14px', background: '#fff', border: `1px solid ${P.bord}`,
                borderRadius: DS.radiusControl, color: P.navy, fontSize: 12, fontWeight: 700,
                fontFamily: DS.fontBody, cursor: 'pointer' }}>
              <Timer size={14} strokeWidth={2} /> Durée
            </button>
          )}
          {ctx.onSupprimerIntervention && (
            <button type="button" data-testid="panneau-intervention-supprimer"
              onClick={() => ctx.onSupprimerIntervention(bloc)} className="fbz-press fbz-tap"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44,
                padding: '9px 14px', background: '#fff', border: `1px solid ${P.bord}`,
                borderRadius: DS.radiusControl, color: P.navy, fontSize: 12, fontWeight: 700,
                fontFamily: DS.fontBody, cursor: 'pointer' }}>
              <X size={14} strokeWidth={2} /> Supprimer l'intervention
            </button>
          )}
        </div>
      </div>
      </div>
      </PortailFeuille>
    )
  }

  // ── PASTILLE D'ÉTAT DU BANDEAU — arbitrage Dimitri 2026-08-19 : la
  // pastille dit l'état du MÉNAGE (« À venir » / « En cours » / « Validé »),
  // la BANDE dit l'état du CONTRÔLE — deux informations distinctes qui
  // COEXISTENT : un ménage validé dont le contrôle a des points porte
  // « Validé » en vert ET la bande terracotta (le ménage a bien été fait,
  // c'est le contrôle qui a trouvé des choses). Couleurs : la couleur pour
  // les états qui veulent dire quelque chose, le neutre pour l'attente.
  const pastilleEtat = fini
    ? { texte: 'Validé', fond: '#7fc4a3', bord: '#1a4f3a', couleur: '#0f3d2a' }
    : run.status === 'in_progress'
      ? { texte: 'En cours', fond: '#87b8e6', bord: '#3d5166', couleur: '#1c2833' }
      : { texte: 'À venir' }

  // (v3 2026-08-19 : le bloc « Qui contrôle » a quitté le panneau — tout le
  // contrôle vit dans le bouton « Contrôler » de la barre ; seul L'ÉTAT reste
  // visible ici, via la BANDE du bandeau.)

  // « C'EST MOI » — l'orchestrateur est l'agent assigné de ce ménage (les
  // agents purs ne passent pas par ce panneau : leur page est la checklist).
  const estMoi = !!(ctx.self && bloc.agent && ctx.self === bloc.agent)

  // « QUI CONTRÔLERA CE MÉNAGE » (Dimitri 2026-08-19) : quand C'EST LUI qui
  // fait le ménage, la barre remplace « Contrôler » par « Checklist » — la
  // désignation revient ICI, sous « Mon travail ». Jamais lui-même dans la
  // liste (on ne contrôle pas son propre travail) ; plus de désignation une
  // fois un contrôle déposé (elle n'a plus d'objet).
  const optionsControleurs = estMoi
    ? (ctx.assignOptions || []).filter((a) => a.value !== ctx.self)
    : []
  const controleurDesigne = run
    ? (run.controleur_designe_email
      || (ctx.controleurDefautParLogement || {})[bloc.logementId] || null)
    : null

  // ── BLOC 3 — les comptes de la checklist (double-forme lignesChecklist).
  const lignes = lignesChecklist(run.items_checked)
  const coches = lignes.filter((l) => l.coche).length
  const total = lignes.length
  const nbPhotos = Array.isArray(run.photos) ? run.photos.length : 0
  const compteComplet = total > 0 && coches >= total

  // ── BLOC 4 — codes + adresse (guichet menage_log_info, héritage serveur).
  const codes = lignesCodes(info?.codes)
  const adresse = info?.adresse || null

  // ── BLOC 5 — incidents : ouverts = en_cours SEULEMENT.
  const ouverts = (incidents || []).filter((i) => normalizeStatus(i.status) === STATUS_INCIDENTS.EN_COURS)
  const regles = (incidents || []).filter((i) => normalizeStatus(i.status) !== STATUS_INCIDENTS.EN_COURS)

  return (
    <PortailFeuille actif={etroit}>
    <div data-testid="panneau-voile"
      style={{ position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(46,61,77,.5)', display: 'flex',
        alignItems: etroit ? 'flex-end' : 'center',
        justifyContent: 'center', padding: etroit ? 0 : 16 }}>
    <div ref={racineRef} data-testid={`panneau-menage-${bloc.id}`}
      // Maquette 2026-08-20 : rayon 15, ombre portée — le panneau respire.
      // 2026-08-19 : fenêtre OPAQUE sur voile sombre (fixed) — l'opacité
      // d'une ligne de frise ne peut plus déteindre sur lui.
      style={{ width: '100%', maxWidth: etroit ? 'none' : 430, maxHeight: '90vh', overflowY: 'auto',
        border: `1px solid ${P.bord}`, borderRadius: etroit ? '18px 18px 0 0' : 15,
        background: '#fff', fontFamily: DS.fontBody,
        boxShadow: '0 10px 34px rgba(35,50,64,.35)' }}>

      {/* ══ LE BANDEAU ══ */}
      <Bandeau nom={bloc.nom}
        // LA PLAGE, pas le dessin du bloc : chez l'orchestrateur elle SUIT LE
        // RÉEL voyageur (ADR-226, garantie verrouillée par la suite
        // guichet-horaires) — l'ancien détail affichait déjà bloc.creneau.
        dateTexte={dateBandeau(run.scheduled_date)}
        heuresTexte={bloc.creneau && !bloc.creneau.sansPlage
          ? libelleHeures(bloc.creneau.debut, bloc.creneau.fin)
          : libelleHeures(bloc.debut, bloc.fin)}
        pastille={pastilleEtat}
        // LES BORNES DU SÉJOUR : départ = début de plage (checkout suivi du
        // réel), arrivée = fin de plage. Une donnée absente SE DIT.
        sejour={{
          departPersonnes: typeof run.guest_count === 'number' ? run.guest_count : undefined,
          // Une borne NON CONNUE (repli 9 h-19 h du dessin) ne s'affiche
          // jamais comme une heure de séjour — la carte dira « heure
          // inconnue » (departConnu/arriveeConnue posés par creneauPossible).
          departHeure: bloc.creneau && !bloc.creneau.sansPlage && bloc.creneau.departConnu !== false
            ? formaterHeureCourte(bloc.creneau.debut) : null,
          arriveePersonnes: undefined,
          arriveeHeure: bloc.creneau && !bloc.creneau.sansPlage && bloc.creneau.arriveeConnue !== false
            ? formaterHeureCourte(bloc.creneau.fin) : null,
        }}
        bande={controle ? {
          ton: (controle.statut === 'a_reprendre' || controle.statut === 'reprise_en_cours')
            ? 'attente' : 'bon',
          ...(function () {
            const n = Array.isArray(controle.points) ? controle.points.length : 0
            const pts = `${n} point${n > 1 ? 's' : ''}`
            const prenomControleur = firstNameFromEmail(controle.controleur_email) || null
            const depuis = depuisTexte(controle.cree_le)
            if (controle.statut === 'bon') {
              return {
                ligne1: prenomControleur ? `Contrôlé par ${prenomControleur} · rien à signaler` : 'Contrôlé · rien à signaler',
                ligne2: null,
              }
            }
            if (controle.statut === 'regle') {
              return {
                ligne1: `${pts} · repris`,
                ligne2: prenomControleur ? `contrôlé par ${prenomControleur}` : null,
              }
            }
            const qui = controle.reprise_par === 'controleur'
              ? (prenomControleur || 'le contrôleur')
              : (run.assigned_provider_email ? (firstNameFromEmail(run.assigned_provider_email) || "l'agent") : "l'agent")
            return {
              ligne1: `${pts} à reprendre`,
              ligne2: `${qui} reprend${depuis ? ` · ${depuis}` : ''}`,
            }
          })(),
          onOuvrir: ctx.onOuvrirControle ? () => ctx.onOuvrirControle(controle) : null,
        } : null} />

      {/* Les blocs ALTERNENT DE FOND (maquette 2026-08-20) — c'est le
          changement de fond qui sépare, plus aucun trait uniforme. */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>

        {/* ══ BLOC 1 · QUI FAIT LE MÉNAGE ══ */}
        <section data-testid="panneau-bloc-menage"
          style={{ display: 'flex', flexDirection: 'column', gap: 8,
            background: '#f4f9fd', padding: '13px 14px' }}>
          <TitreBloc icone={User} fond="#5c8ab8"
            couleur="#3d5166">Qui fait le ménage</TitreBloc>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'stretch' }}>
            <RangeeAgents options={ctx.assignOptions || []} actifEmail={bloc.agent}
              teinte="ciel" testPrefix="panneau-agent"
              onChoisir={(email) => {
                if (email !== bloc.agent && ctx.onReassign) ctx.onReassign(run, email)
              }} />
            {/* RETIRER l'assignation — carré 44 px terracotta, même chemin
                serveur que le menu (assigné à null). Sans assigné : inactif. */}
            <button type="button" data-testid="panneau-retirer"
              aria-label="Retirer l'assignation" title="Retirer l'assignation"
              disabled={!bloc.agent}
              onClick={() => ctx.onRetirerAssignation && ctx.onRetirerAssignation(run)}
              className="fbz-press"
              style={{ width: 44, height: 44, flexShrink: 0, border: 'none',
                background: P.terra, opacity: bloc.agent ? 1 : .45,
                borderRadius: 10, color: '#fff', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
                cursor: bloc.agent ? 'pointer' : 'not-allowed' }}>
              <X size={17} strokeWidth={2.4} />
            </button>
          </div>
        
          {/* ② L'ACCUSÉ DE RÉCEPTION (réintégré, arbitrage Dimitri 2026-08-19) :
              mon assignation est-elle ARRIVÉE ? Ligne discrète, données de
              l'événement par run (evenementsParRun — le type reste en infobulle). */}
          {run && ctx.evenementsParRun?.[bloc.id] && (() => {
            const evt = ctx.evenementsParRun[bloc.id]
            const prenom = libelleDepuisEmail(evt.destinataire_email)
            return (
              <div data-testid={`panneau-accuse-${bloc.id}`}
                title={{ retrait: 'retrait', ajout: 'ajout', heure: 'horaire modifié' }[evt.type] || evt.type}
                style={evt.vu_le
                  ? { fontSize: 10.5, color: P.muet }
                  // AMBRE (maquette 2026-08-20) : une ATTENTE, pas un texte neutre.
                  : { display: 'inline-flex', alignItems: 'center', gap: 6,
                      alignSelf: 'flex-start', fontSize: 10.5, fontWeight: 600,
                      background: '#fdf7ee', color: '#8a5f19', borderRadius: 7,
                      padding: '4px 9px' }}>
                {evt.vu_le
                  ? `${prenom} a vu ce ménage`
                  : (<>
                    <EyeOff size={12} strokeWidth={2.1} color="#9a6a1c" />
                    {`${prenom} n'a pas encore vu ce ménage`}
                  </>)}
              </div>
            )
          })()}
          {/* ③ L'EXCEPTION À LA RÈGLE + « Revenir à la règle » (réintégrés) :
              une décision prise sur CE ménage se défait là où elle se voit. */}
          {run && exception && (
            <div data-testid={`suivi-exception-${run.id}`} style={{
              alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
              background: DS.corailSoft, color: DS.corailText, border: `1px solid ${DS.corail}66`,
              padding: '5px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 700 }}>
              {exception}
            </div>
          )}
          {run && retourRegle && retourRegle.possible && (
            <button type="button" data-testid={`suivi-retour-regle-${run.id}`} className="fbz-press"
              onClick={() => setConfirmRetourRegle(true)}
              style={{ alignSelf: 'flex-start', padding: '7px 12px', borderRadius: DS.radiusControl,
                border: `1px solid ${P.cielBord}`, background: '#fff', color: P.gris,
                fontSize: 12, fontWeight: 700, fontFamily: DS.fontBody, cursor: 'pointer' }}>
              Revenir à la règle
            </button>
          )}
          {run && retourRegle && retourRegle.possible && confirmRetourRegle && (
            <FeuilleConfirmation
              testId={`suivi-retour-regle-confirm-${run.id}`}
              titre="Rendre ce ménage à l'agent de la règle ?"
              texte={`${ctx.nomAgent ? (ctx.nomAgent(retourRegle.cible) || retourRegle.cible) : retourRegle.cible} reprendra ce ménage. L'exception posée sur ce ménage disparaît ; la règle du logement continue de s'appliquer.`}
              libelleConfirmer="Rendre le ménage"
              libelleRenoncer="Le garder"
              ton="action"
              onConfirmer={() => { setConfirmRetourRegle(false); if (ctx.onReassign) ctx.onReassign(run, retourRegle.cible) }}
              onRenoncer={() => setConfirmRetourRegle(false)} />
          )}
        </section>

        {/* ══ BLOC 3 · LE TRAVAIL DE L'AGENT ══
            Bascule « c'est moi » (Dimitri 2026-08-19, orchestrateur assigné) :
            le titre parle à la première personne — « Mon travail » — et le
            bouton devient un GESTE (« Remplir la checklist », dégradé ciel
            plein, texte blanc) : une action, plus une consultation. */}
        <section data-testid="panneau-bloc-travail"
          style={{ display: 'flex', flexDirection: 'column', gap: 8,
            background: '#fff', padding: '13px 14px' }}>
          <TitreBloc icone={ClipboardList} fond="#87b8e6" couleurIcone="#1c2833"
            couleur="#3d5166">{estMoi ? 'Mon travail' : "Le travail de l'agent"}</TitreBloc>
          {!fini ? (
            <BlocExplique testId="panneau-explique-checklist" pointille
              texte="La checklist se remplit pendant le ménage." />
          ) : (
            <button type="button" data-testid="panneau-checklist"
              onClick={() => ctx.onOpenChecklist && ctx.onOpenChecklist(run)}
              className="fbz-press fbz-tap"
              style={{ width: '100%', minHeight: 44,
                background: estMoi
                  ? 'linear-gradient(100deg, #5c8ab8 0%, #87b8e6 100%)' : '#fff',
                border: estMoi ? 'none' : `1px solid ${P.ciel}`,
                borderRadius: 9, cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 2, padding: '8px 12px',
                fontFamily: DS.fontBody }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                fontSize: 12.5, fontWeight: 700, color: estMoi ? '#fff' : P.navy }}>
                <ClipboardList size={14} strokeWidth={2.2} />
                {estMoi ? 'Remplir la checklist' : 'Voir la checklist'}
              </span>
              <span data-testid="panneau-checklist-comptes"
                style={{ fontSize: 10.5, fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: estMoi ? 'rgba(255,255,255,.9)'
                    : compteComplet ? P.vertSombre : P.terraTexte }}>
                {coches} sur {total} · {nbPhotos} photo{nbPhotos > 1 ? 's' : ''}
              </span>
            </button>
          )}
          {estMoi && !controle && (
            <div data-testid="panneau-qui-controlera"
              style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.07em',
                textTransform: 'uppercase', color: '#3d5166', opacity: .85 }}>
                Qui contrôlera ce ménage
              </span>
              {optionsControleurs.length > 0 ? (
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  <RangeeAgents options={optionsControleurs} actifEmail={controleurDesigne}
                    teinte="vert" testPrefix="panneau-controleur"
                    onChoisir={(email) => {
                      if (email !== run.controleur_designe_email && ctx.onDesignerControleur) {
                        ctx.onDesignerControleur(run, email)
                      }
                    }} />
                </div>
              ) : (
                <span data-testid="panneau-controleur-personne"
                  style={{ fontSize: 11.5, color: P.muet }}>
                  Aucun autre contrôleur à désigner — le contrôle se prendra
                  depuis « À décider → Contrôle ».
                </span>
              )}
            </div>
          )}
        
          {/* ④ LA DURÉE DE CE MÉNAGE (réintégrée) : la ligne + le moyen de la
              modifier (la modale existante, comme avant). */}
          {run && (
            <div data-testid="panneau-duree"
              style={{ display: 'flex', alignItems: 'center', gap: 10,
                background: 'linear-gradient(100deg, #e8f2fa 0%, #f4f9fd 100%)',
                borderRadius: 10, padding: '9px 12px' }}>
              <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 9,
                background: '#5c8ab8', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0 }}>
                <Hourglass size={15} strokeWidth={2.1} color="#fff" />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700,
                  color: P.gris, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                  Durée prévue
                </span>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: P.navy }}>
                  {formaterDuree(bloc.dureeMin)}
                </span>
              </span>
              {ctx.onOuvrirDuree && (
                <button type="button" data-testid="panneau-duree-modifier"
                  onClick={() => ctx.onOuvrirDuree(bloc)} className="fbz-press"
                  style={{ flexShrink: 0, minHeight: 32, padding: '5px 12px',
                    border: `1px solid ${P.cielBord}`, background: '#fff', borderRadius: 8,
                    color: P.navy, fontSize: 10.5, fontWeight: 700, cursor: 'pointer',
                    fontFamily: DS.fontBody }}>
                  Modifier
                </button>
              )}
            </div>
          )}
        </section>

        {/* ══ BLOC 4 · LE LOGEMENT ══ */}
        <section data-testid="panneau-bloc-logement"
          style={{ display: 'flex', flexDirection: 'column', gap: 8,
            background: '#f2f7fb', padding: '13px 14px' }}>
          <TitreBloc icone={Home} fond="#3d5166"
            couleur="#3d5166">Le logement</TitreBloc>
          {codes.length === 0 ? (
            <div data-testid="panneau-aucun-code"
              style={{ fontSize: 11.5, color: P.gris, background: P.expliqueFond,
                borderRadius: 8, padding: '9px 11px' }}>
              Aucun code renseigné pour ce logement
            </div>
          ) : codes.map(({ cle, label, code }) => (
            /* LA CARTE NAVY PLEINE (maquette 2026-08-20) — l'information la
               plus lue du panneau est la plus forte. Un code par carte. */
            <div key={cle} data-testid={`panneau-code-${cle}`}
              style={{ display: 'flex', alignItems: 'center', gap: 11,
                background: `linear-gradient(115deg, ${P.navy} 0%, ${P.ciel} 100%)`,
                borderRadius: 11, padding: '10px 12px' }}>
              <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 10,
                background: 'rgba(255,255,255,.16)', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Key size={16} strokeWidth={2.1} color="#fff" />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700,
                  color: 'rgba(255,255,255,.72)', textTransform: 'uppercase',
                  letterSpacing: '.09em' }}>
                  {label}
                </span>
                <span data-testid={`panneau-code-valeur-${cle}`}
                  style={{ display: 'block', fontSize: 21, fontWeight: 700, color: '#fff',
                    letterSpacing: '.15em', fontVariantNumeric: 'tabular-nums',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {code}
                </span>
              </span>
              <button type="button" data-testid={`panneau-copier-${cle}`}
                onClick={() => copier(cle, code)} className="fbz-press"
                aria-label={`Copier le code ${label.toLowerCase()}`}
                style={{ flexShrink: 0, minHeight: 32, padding: '5px 12px',
                  border: '1px solid rgba(255,255,255,.3)', background: 'rgba(255,255,255,.12)',
                  borderRadius: 8, fontSize: 10.5, fontWeight: 700, color: '#fff',
                  cursor: 'pointer', fontFamily: DS.fontBody }}>
                {copie[cle] === 'ok' ? 'Copié' : copie[cle] === 'echec' ? 'Copie impossible' : 'Copier'}
              </button>
            </div>
          ))}
          {adresse && (
            <div data-testid="panneau-adresse"
              style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff',
                border: `1px solid ${P.bord}`, borderRadius: 8, padding: '8px 11px' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: P.navy,
                lineHeight: 1.45 }}>{adresse}</span>
              <a data-testid="panneau-itineraire"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(adresse)}`}
                target="_blank" rel="noopener noreferrer"
                aria-label={`Itinéraire vers ${bloc.nom}`} className="fbz-press"
                style={{ flexShrink: 0, width: 40, height: 40, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', background: '#fff',
                  border: `1px solid ${P.cielBord}`, borderRadius: 9, color: P.ciel,
                  textDecoration: 'none' }}>
                <Navigation size={16} strokeWidth={2.2} />
              </a>
            </div>
          )}
        </section>

        {/* ══ BLOC 5 · LES INCIDENTS (arbitrage Dimitri 2026-08-19) : le bloc
            ne disparaît JAMAIS — vide, il le dit ; et « Signaler un incident »
            vit en pied, TOUJOURS (c'est quand il n'y a rien qu'on veut ajouter
            le premier). ══ */}
        {(
          <section data-testid="panneau-bloc-incidents"
            style={{ display: 'flex', flexDirection: 'column', gap: 6,
              background: '#fdf6f3', padding: '13px 14px' }}>
            <TitreBloc icone={AlertTriangle} fond="#b8412b"
              couleur="#8f2f1d" testId="panneau-incidents-titre">
              {ouverts.length > 0
                ? `Incidents · ${ouverts.length} ouvert${ouverts.length > 1 ? 's' : ''}`
                : 'Incidents'}
            </TitreBloc>
            {ouverts.map((inc) => (
              <CarteIncident key={inc.id} incident={inc} ouvert
                onOuvrir={ctx.onNavigate ? () => ctx.onNavigate('incidents') : null} />
            ))}
            {regles.length > 2 && !reglesDeplies ? (
              <button type="button" data-testid="panneau-incidents-repli"
                onClick={() => setReglesDeplies(true)} className="fbz-press"
                aria-expanded={false}
                style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                  background: '#fff', border: `1px solid ${P.regleBord}`, borderRadius: 8,
                  padding: '8px 11px', cursor: 'pointer', fontFamily: DS.fontBody }}>
                <CircleCheck size={14} strokeWidth={2.2} color={P.vert} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left', fontSize: 11.5, color: P.gris }}>
                  {regles.length} incidents réglés
                </span>
                <ChevronDown size={14} strokeWidth={2.2} color={P.muet} style={{ flexShrink: 0 }} />
              </button>
            ) : (
              regles.map((inc) => (
                <CarteIncident key={inc.id} incident={inc} ouvert={false}
                  onOuvrir={ctx.onNavigate ? () => ctx.onNavigate('incidents') : null} />
              ))
            )}
            {/* « Aucun incident » N'EST DIT QUE SUR (batch réussi ET vide) — un
                échec de lecture (null) n'affirme rien : une absence n'est pas
                une cause. Le bouton, lui, reste dans les deux cas. */}
            {Array.isArray(incidents) && incidents.length === 0 && (
              /* UNE BONNE NOUVELLE (maquette 2026-08-20), pas un vide. */
              <div data-testid="panneau-incidents-vide"
                style={{ display: 'flex', alignItems: 'center', gap: 10,
                  background: '#f4faf7', border: '1px solid #c9e0d4',
                  borderRadius: 10, padding: '9px 12px' }}>
                <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: '50%',
                  background: '#7fc4a3', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0 }}>
                  <Check size={16} strokeWidth={2.6} color="#fff" />
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#124a33' }}>
                  Aucun incident sur ce ménage
                </span>
              </div>
            )}
            {/* ① « SIGNALER UN INCIDENT » (réintégré) — pleine largeur, blanc
                bordé terracotta, 44 px, TOUJOURS affiché ; indisponible → la
                raison en toutes lettres, jamais un bouton muet. */}
            {run && (() => {
              const autorise = ctx.incidentAutorise ? ctx.incidentAutorise(run) : false
              return (
                <button type="button" data-testid="panneau-signaler-incident"
                  disabled={!autorise}
                  onClick={() => autorise && ctx.onSignalIncident
                    && ctx.onSignalIncident(run, ctx.statutEffectif ? ctx.statutEffectif(run) : null)}
                  className="fbz-press fbz-tap"
                  style={{ width: '100%', minHeight: 44, marginTop: 4,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    // v3 : EN CONTOUR, jamais en plein — action rare, elle ne
                    // tire pas l'œil.
                    gap: 7, background: '#fff',
                    border: `1px solid ${autorise ? '#e0b5a5' : P.regleBord}`,
                    borderRadius: 9, color: autorise ? P.terraSombre : P.muet,
                    fontSize: 12.5, fontWeight: 700, fontFamily: DS.fontBody,
                    cursor: autorise ? 'pointer' : 'default' }}>
                  <AlertTriangle size={15} strokeWidth={2.1} />
                  {autorise ? 'Signaler un incident' : 'Signalement disponible le jour du ménage'}
                </button>
              )
            })()}
          </section>
        )}
      </div>
    </div>
    </div>
    </PortailFeuille>
  )
}

// ── Le bandeau navy : dégradé 115deg + deux disques translucides. ───────────
// v3 2026-08-19 : le bandeau porte AUSSI les deux cartes séjour (départ /
// arrivée — l'information qui BORNE le ménage) et la BANDE d'état du contrôle
// (cliquable → le détail complet, le même que la pastille œil).
function Bandeau({ nom, dateTexte, heuresTexte, sousTitre, pastille, sejour, bande }) {
  return (
    <div data-testid="panneau-bandeau"
      style={{ position: 'relative', overflow: 'hidden', padding: '14px 16px',
        background: `linear-gradient(115deg, ${P.navyFonce} 0%, ${P.navy} 38%, ${P.ciel} 100%)`,
        display: 'flex', alignItems: 'center', gap: 10 }}>
      <span aria-hidden="true" style={{ position: 'absolute', top: -34, right: -22,
        width: 96, height: 96, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
      <span aria-hidden="true" style={{ position: 'absolute', bottom: -30, left: '38%',
        width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nom}</div>
        {(dateTexte || heuresTexte || sousTitre) && (
          <div data-testid="panneau-bandeau-sous-titre"
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5,
              color: 'rgba(255,255,255,.78)', marginTop: 2 }}>
            {dateTexte ? (
              <>
                <CalendarDays size={12} strokeWidth={2} style={{ flexShrink: 0 }} />
                <span>{dateTexte}</span>
                {heuresTexte && (
                  <>
                    <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%',
                      background: 'rgba(255,255,255,.4)', flexShrink: 0 }} />
                    <span>{heuresTexte}</span>
                  </>
                )}
              </>
            ) : sousTitre}
          </div>
        )}
        </div>
        {pastille && (
          <span data-testid="panneau-pastille-etat"
            style={{ position: 'relative', flexShrink: 0, fontSize: 10.5, fontWeight: 700,
              background: pastille.fond || 'rgba(255,255,255,.14)',
              border: `1px solid ${pastille.bord || 'rgba(255,255,255,.24)'}`,
              color: pastille.couleur || '#fff', borderRadius: 20, padding: '4px 10px' }}>
            {pastille.texte}
          </span>
        )}
        </div>
        {sejour && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <CarteSejour testId="panneau-sejour-depart" etiquette="Départ"
              icone={LogOut} personnes={sejour.departPersonnes} heure={sejour.departHeure} />
            <CarteSejour testId="panneau-sejour-arrivee" etiquette="Arrivée"
              icone={LogIn} personnes={sejour.arriveePersonnes} heure={sejour.arriveeHeure} />
          </div>
        )}
        <BandeControle bande={bande} />
      </div>
    </div>
  )
}

// Les DEUX CARTES SÉJOUR translucides — une donnée manquante SE DIT, la carte
// ne disparaît pas.
function CarteSejour({ testId, etiquette, icone: Icone, personnes, heure }) {
  // L'heure manquante SE DIT même quand le nombre de personnes est connu —
  // « 2 pers. » seul laisserait croire que l'heure n'existe pas.
  const valeur = [
    typeof personnes === 'number' ? `${personnes} pers.` : null,
    heure || 'heure inconnue',
  ].filter(Boolean).join(' · ')
  return (
    <div data-testid={testId}
      style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,.13)',
        borderRadius: 10, padding: '7px 10px' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9,
        fontWeight: 700, color: 'rgba(255,255,255,.62)', textTransform: 'uppercase',
        letterSpacing: '.08em' }}>
        <Icone size={11} strokeWidth={2.2} /> {etiquette}
      </span>
      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff',
        marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {valeur}
      </span>
    </div>
  )
}

// LA BANDE D'ÉTAT DU CONTRÔLE — cliquable, elle ouvre le même détail que la
// pastille œil. Aucun contrôle → pas de bande.
function BandeControle({ bande }) {
  if (!bande) return null
  return (
    <button type="button" data-testid="panneau-bande-controle"
      onClick={bande.onOuvrir || undefined} className="fbz-press"
      style={{ position: 'relative', width: '100%', display: 'flex',
        alignItems: 'center', gap: 9, textAlign: 'left', marginTop: 9,
        background: bande.ton === 'attente' ? 'rgba(184,65,43,.9)' : 'rgba(31,90,67,.9)',
        border: '1px solid rgba(255,255,255,.22)', borderRadius: 10,
        padding: '8px 11px', cursor: bande.onOuvrir ? 'pointer' : 'default',
        fontFamily: DS.fontBody }}>
      <Eye size={15} strokeWidth={2.2} color="#fff" style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#fff' }}>
          {bande.ligne1}
        </span>
        {bande.ligne2 && (
          <span style={{ display: 'block', fontSize: 10.5, color: 'rgba(255,255,255,.82)' }}>
            {bande.ligne2}
          </span>
        )}
      </span>
      <ChevronRight size={14} strokeWidth={2.2} color="rgba(255,255,255,.7)"
        style={{ flexShrink: 0 }} />
    </button>
  )
}

// ── Une carte d'incident — ouverte (terracotta) ou réglée (blanche). ────────
function CarteIncident({ incident, ouvert, onOuvrir }) {
  const prenom = firstNameFromEmail(incident.reported_by) || null
  const age = ilYATexte(incident.created_at)
  const signe = [prenom ? `signalé par ${prenom}` : 'signalé', age].filter(Boolean).join(' · ')
  const Interieur = (
    <>
      {ouvert
        ? <AlertTriangle size={15} strokeWidth={2.2} color={P.terra} style={{ flexShrink: 0 }} />
        : <CircleCheck size={15} strokeWidth={2.2} color={P.vert} style={{ flexShrink: 0 }} />}
      <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 700,
          color: ouvert ? P.terraSombre : P.gris, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {incident.label || 'Incident'}
        </span>
        <span data-testid={`panneau-incident-signe-${incident.id}`}
          style={{ display: 'block', fontSize: 10.5, marginTop: 1,
            color: ouvert ? P.incidentSigne : P.muet }}>
          {signe}
        </span>
      </span>
      {onOuvrir && (
        <ChevronRight size={15} strokeWidth={2.2}
          color={ouvert ? P.incidentSigne : P.muet} style={{ flexShrink: 0 }} />
      )}
    </>
  )
  const style = {
    display: 'flex', alignItems: 'center', gap: 9, width: '100%',
    background: ouvert ? P.incidentFond : '#fff',
    border: `1px solid ${ouvert ? P.incidentBord : P.regleBord}`,
    borderRadius: 8, padding: '8px 11px', fontFamily: DS.fontBody,
  }
  // PAS DE LISERÉ VERTICAL (maquette) — la carte se dit par son fond seul.
  return onOuvrir ? (
    <button type="button" data-testid={`panneau-incident-${incident.id}`}
      onClick={onOuvrir} className="fbz-press"
      style={{ ...style, cursor: 'pointer', textAlign: 'left' }}>
      {Interieur}
    </button>
  ) : (
    <div data-testid={`panneau-incident-${incident.id}`} style={style}>
      {Interieur}
    </div>
  )
}
